import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { BTN_PRIMARY, BTN_GHOST_DARK, HOME_WRAP } from "./homeTheme";
import HeroProductShowcase from "./HeroProductShowcase";
import { imageUrl } from "@/lib/imageUrl";

/**
 * Homepage hero. Copy comes from `data.hero` (Admin → Home Page): eyebrow, headline (+ highlighted last word), lead
 * and two CTAs. The visual is a curated product showcase (HeroProductShowcase) — real Shingare products from several
 * families — instead of a single background photo, so the first screen communicates the breadth of the range.
 * `hero.backgroundImage` is an OPTIONAL extra: when the admin ticks "Show this image behind the hero" (`hero.useBackgroundImage`)
 * the chosen image is placed behind the navy stage, under the same navy tint. Untouched (the default) the hero looks exactly as approved.
 *
 * Layout: copy left, product composition right on desktop; stacked on tablet/phone. Motion (all disabled for
 * prefers-reduced-motion): the copy fades and slides in once and drifts a few pixels with scroll. The headline's
 * sentences are split onto their own lines so "Engineering Tools. / Built for / Performance." reads as three beats.
 */
export default function HomeHero({ data }) {
  const hero = data?.hero || {};
  const reduce = useReducedMotion();
  const { scrollY } = useScroll();
  const copyY = useTransform(scrollY, [0, 800], [0, -30]);

  const lines = String(hero.mainHeading || "").trim().split(/(?<=[.!?])\s+/).filter(Boolean);
  const highlight = String(hero.highlightedText || "").trim();
  const backdrop = hero.useBackgroundImage && hero.backgroundImage ? imageUrl(hero.backgroundImage) : "";
  if (!lines.length && !highlight) return null;

  const fade = (delay) => (reduce ? {} : { initial: { opacity: 0, y: 28 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] } });

  return (
    <section className="relative isolate overflow-hidden bg-[#071B3A]" aria-label="Introduction">
      {/* deep-navy stage: gentle diagonal gradient, blue light behind the products, a faint engineering grid */}
      {backdrop && <img src={backdrop} alt="" aria-hidden="true" data-testid="hero-backdrop" decoding="async" fetchPriority="high" className="absolute inset-0 -z-20 h-full w-full object-cover" />}
      <div className={`absolute inset-0 -z-10 bg-[linear-gradient(115deg,#071B3A_0%,#0A2149_55%,#0B2348_100%)] ${backdrop ? "opacity-[0.84]" : ""}`} aria-hidden="true" />
      <div className="pointer-events-none absolute -right-40 top-1/2 -z-10 h-[720px] w-[720px] -translate-y-1/2 rounded-full bg-[#1769FF]/[0.22] blur-[150px]" aria-hidden="true" />
      <div className="pointer-events-none absolute inset-0 -z-10 opacity-[0.06] [background-image:linear-gradient(to_right,#fff_1px,transparent_1px),linear-gradient(to_bottom,#fff_1px,transparent_1px)] [background-size:64px_64px] [mask-image:radial-gradient(ellipse_75%_85%_at_72%_50%,#000,transparent)]" aria-hidden="true" />
      <div className="absolute inset-x-0 bottom-0 -z-10 h-32 bg-gradient-to-t from-[#071B3A]/70 to-transparent" aria-hidden="true" />

      <div className={`${HOME_WRAP} grid items-center gap-5 pb-[100px] pt-7 sm:gap-9 sm:pt-12 md:grid-cols-[minmax(0,1.06fr)_minmax(0,1fr)] md:gap-5 md:pb-28 md:pt-10 lg:min-h-[640px] lg:gap-6 lg:pb-32 lg:pt-14 xl:gap-10`}>
        <motion.div className="max-w-[660px]" style={reduce ? undefined : { y: copyY }}>
          {hero.smallHeading && (
            <motion.div {...fade(0.05)} className="mb-4 flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#7FB0FF] sm:mb-6 sm:text-[13px] sm:tracking-[0.22em]">
              <span className="h-px w-10 bg-[#1769FF]" aria-hidden="true" />
              {hero.smallHeading}
            </motion.div>
          )}
          <motion.h1 {...fade(0.15)} className="font-heading text-[38px] font-semibold leading-[1.05] tracking-[-0.03em] text-white sm:text-[52px] md:text-[35px] lg:text-[48px] xl:text-[62px]">
            {lines.map((l) => (
              <span key={l} className="block">{l}</span>
            ))}
            {highlight && <span className="block text-[#3B82FF]">{highlight}</span>}
          </motion.h1>
          {hero.description && (
            <motion.p {...fade(0.28)} className="mt-4 max-w-[540px] text-[14.5px] leading-relaxed text-white/75 sm:mt-6 sm:text-lg md:text-[14px] lg:text-lg">{hero.description}</motion.p>
          )}
          <motion.div {...fade(0.4)} className="mt-6 flex flex-col gap-2.5 sm:mt-10 sm:flex-row sm:gap-4 md:mt-7 md:flex-col md:items-start lg:mt-10 lg:flex-row">
            {hero.primaryButton?.label && (
              <Link to={hero.primaryButton.link || "/products"} className={BTN_PRIMARY} data-testid="hero-primary-cta">
                {hero.primaryButton.label}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
              </Link>
            )}
            {hero.secondaryButton?.label && (
              <Link to={hero.secondaryButton.link || "/contact"} className={BTN_GHOST_DARK} data-testid="hero-secondary-cta">
                {hero.secondaryButton.label}
              </Link>
            )}
          </motion.div>
        </motion.div>

        <HeroProductShowcase items={data?.heroShowcase} />
      </div>
    </section>
  );
}
